import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  CHART_AXIS_STROKE,
  CHART_GRID_STROKE,
  CHART_PRIMARY_COLOR,
  CHART_TICK_COLOR,
} from "src/components/charts/chartPalette";
import RestaurantSectionCard from "../RestaurantSectionCard";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";
import type { RestaurantMetrics } from "../../utils/restaurantMetrics";

const ORDERS_LINE_COLOR = "#f6c15f";

type Granularity = "daily" | "weekly";

interface RestaurantRevenueOrdersTrendProps {
  metrics: RestaurantMetrics;
}

const RestaurantRevenueOrdersTrend: FC<RestaurantRevenueOrdersTrendProps> = ({ metrics }) => {
  const { t } = useTranslation();
  const fmt = useRestaurantFormatters();
  const [granularity, setGranularity] = useState<Granularity>("daily");
  const data = granularity === "daily" ? metrics.daily : metrics.weekly;

  return (
    <RestaurantSectionCard
      title={t("restaurantAnalytics.charts.revenueOrdersTrend", { defaultValue: "Revenue & orders trend" })}
      tip={t("restaurantAnalytics.charts.revenueOrdersTrendTip", {
        defaultValue: "Daily or weekly revenue (bars) plotted against order volume (line).",
      })}
      action={(
        <div className="restaurant-chart-toggle" role="group">
          {(["daily", "weekly"] as const).map(option => (
            <button
              key={option}
              type="button"
              className={`restaurant-chart-toggle__btn ${granularity === option ? "restaurant-chart-toggle__btn--active" : ""}`}
              onClick={() => setGranularity(option)}
            >
              {option === "daily"
                ? t("restaurantAnalytics.charts.daily", { defaultValue: "Daily" })
                : t("restaurantAnalytics.charts.weekly", { defaultValue: "Weekly" })}
            </button>
          ))}
        </div>
      )}
    >
      <div className="restaurant-chart restaurant-chart--trend">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: CHART_TICK_COLOR }}
              axisLine={{ stroke: CHART_AXIS_STROKE }}
              tickLine={false}
              minTickGap={24}
              reversed={fmt.isRTL}
              tickFormatter={value => fmt.formatDate(String(value))}
            />
            <YAxis
              yAxisId="revenue"
              orientation={fmt.isRTL ? "right" : "left"}
              width={72}
              tick={{ fontSize: 11, fill: CHART_TICK_COLOR }}
              axisLine={{ stroke: CHART_AXIS_STROKE }}
              tickLine={false}
              tickFormatter={value => fmt.formatCurrency(Number(value), { compact: true })}
            />
            <YAxis
              yAxisId="orders"
              orientation={fmt.isRTL ? "left" : "right"}
              width={48}
              tick={{ fontSize: 11, fill: CHART_TICK_COLOR }}
              axisLine={{ stroke: CHART_AXIS_STROKE }}
              tickLine={false}
              tickFormatter={value => fmt.formatNumber(Number(value))}
            />
            <Tooltip
              content={
                <CustomChartTooltip
                  formatLabel={label => fmt.formatDate(String(label))}
                  formatTooltip={(value, key) =>
                    key === "orders" ? fmt.formatNumber(value) : fmt.formatCurrency(value)}
                />
              }
              wrapperStyle={{ pointerEvents: "none" }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              iconSize={8}
              formatter={value => <span className="restaurant-chart__legend-label">{value}</span>}
            />
            <Bar
              yAxisId="revenue"
              dataKey="revenue"
              name={t("restaurantAnalytics.charts.revenue", { defaultValue: "Revenue" })}
              fill={CHART_PRIMARY_COLOR}
              radius={[4, 4, 0, 0]}
              maxBarSize={22}
            />
            <Line
              yAxisId="orders"
              type="monotone"
              dataKey="orders"
              name={t("restaurantAnalytics.charts.orders", { defaultValue: "Orders" })}
              stroke={ORDERS_LINE_COLOR}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </RestaurantSectionCard>
  );
};

export default RestaurantRevenueOrdersTrend;
